import React, { useState, useEffect } from "react";
// Supprimez useNavigate si vous ne l'utilisez pas
// import { useNavigate } from 'react-router-dom';
import userService from "../../services/userService";
import { useAuth } from "../../context/AuthContext";
import { FaEdit, FaTrash, FaLock, FaUnlock } from "react-icons/fa";
import LoadingSpinner from "../common/LoadingSpinner";
import UserForm from "./UserForm";

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalError, setModalError] = useState(null); // Ajoutez cette ligne
  const [showModal, setShowModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { currentUser: authUser } = useAuth();
  // Supprimez cette ligne car navigate n'est pas utilisé
  // const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  // Le reste du code reste inchangé
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getUsers();
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError("Erreur lors du chargement des utilisateurs");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setCurrentUser(user);
    setShowModal(true);
  };

  const handleCreate = () => {
    setCurrentUser(null);
    setShowModal(true);
  };

  const handleFormSubmit = async (userData) => {
    try {
      setModalError(null); // Réinitialiser l'erreur au début

      if (currentUser) {
        // Mise à jour d'un utilisateur existant
        await userService.updateUser(currentUser._id, userData);
      } else {
        // Création d'un nouvel utilisateur
        await userService.createUser(userData);
      }

      setShowModal(false);
      fetchUsers();
    } catch (err) {
      console.error("Erreur lors de l'enregistrement:", err);
      setModalError(
        err.response?.data?.error ||
          "Une erreur s'est produite lors de l'enregistrement"
      );
    }
  };

  const handleDeleteClick = (user) => {
    setConfirmDelete(user);
  };

  const handleDeleteConfirm = async () => {
    try {
      await userService.deleteUser(confirmDelete._id);
      setConfirmDelete(null);
      fetchUsers();
    } catch (err) {
      console.error("Erreur lors de la suppression:", err);
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await userService.updateUser(user._id, { isActive: !user.isActive });
      fetchUsers();
    } catch (err) {
      console.error("Erreur lors de la modification du statut:", err);
      setError(
        err.response?.data?.error ||
          "Une erreur s'est produite lors de la modification du statut"
      );
    }
  };

  if (loading)
    return <LoadingSpinner message="Chargement des utilisateurs..." />;

  return (
    <div className="container mx-auto px-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Gestion des utilisateurs</h2>
        <button
          onClick={handleCreate}
          className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded"
        >
          Ajouter un utilisateur
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Le reste du code reste inchangé */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Nom d'utilisateur
              </th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Rôle
              </th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Dernière connexion
              </th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">{user.username}</td>
                <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs rounded ${
                      user.role === "admin"
                        ? "bg-red-100 text-red-800"
                        : user.role === "admin_assistant"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {user.role === "admin"
                      ? "Administrateur"
                      : user.role === "admin_assistant"
                      ? "Assistant admin"
                      : "Visiteur"}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs rounded ${
                      user.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {user.isActive ? "Actif" : "Inactif"}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.lastLogin
                    ? new Date(user.lastLogin).toLocaleString()
                    : "Jamais connecté"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                  {/* Ne pas afficher les actions de modification pour les administrateurs si l'utilisateur courant est assistant */}
                  {!(
                    authUser.role === "admin_assistant" && user.role === "admin"
                  ) && (
                    <>
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <FaEdit className="inline-block" /> Modifier
                      </button>
                      <button
                        onClick={() => handleToggleStatus(user)}
                        className={`${
                          user.isActive
                            ? "text-yellow-600 hover:text-yellow-900"
                            : "text-green-600 hover:text-green-900"
                        }`}
                      >
                        {user.isActive ? (
                          <>
                            <FaLock className="inline-block" /> Désactiver
                          </>
                        ) : (
                          <>
                            <FaUnlock className="inline-block" /> Activer
                          </>
                        )}
                      </button>
                      {/* Seul un admin peut supprimer des utilisateurs */}
                      {authUser.role === "admin" && (
                        <button
                          onClick={() => handleDeleteClick(user)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FaTrash className="inline-block" /> Supprimer
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal de création/modification d'utilisateur */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-4">
              {currentUser
                ? "Modifier l'utilisateur"
                : "Ajouter un utilisateur"}
            </h3>
            <UserForm
              user={currentUser}
              onSubmit={handleFormSubmit}
              onCancel={() => setShowModal(false)}
            />
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-4">Confirmer la suppression</h3>
            <p className="mb-4">
              Êtes-vous sûr de vouloir supprimer l'utilisateur{" "}
              {confirmDelete.username} ? Cette action est irréversible.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de création/modification d'utilisateur */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-4">
              {currentUser
                ? "Modifier l'utilisateur"
                : "Ajouter un utilisateur"}
            </h3>

            {modalError && ( // Ajoutez ce bloc
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {modalError}
              </div>
            )}

            <UserForm
              user={currentUser}
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setModalError(null); // Réinitialiser l'erreur à la fermeture
                setShowModal(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
